<template>
	<div>
		<h2 class="content-block">Groups Management</h2>

		<div class="content-block">
			<div class="dx-info-message">
				<i class="dx-icon-info"></i><div class="dx-info-message-text">Temporary interface for group administration. Use the green + button to create a new group. The slug needs to be alphabet/number/underscore only!</div>
			</div>
			<br>
		</div>

		<dx-data-grid style="width:100%;"
			class="dx-card wide-card"
			:data-source="dataSourceConfig"
			:focused-row-index="0"
			:show-borders="false"
			:focused-row-enabled="true"
			:column-auto-width="false"
			:column-hiding-enabled="true"
			:allowColumnResizing="true" columnResizingMode="widget"
			@exporting="onExporting"
		>
			<dx-state-storing :enabled="false" type="localStorage" storage-key="manage-employees" />
			<dx-column-chooser :enabled="true" mode="select" :searchEnabled="true" height="400px">
				<dx-position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
			</dx-column-chooser>
			<dx-paging :page-size="10" />
			<dx-pager :show-page-size-selector="true" :show-info="true" />
			<dx-filter-row :visible="true" />
			<dx-header-filter :visible="true" />
			<dx-search-panel :visible="true" width="300px" />
			<dx-toolbar>
				<dx-item location="before" name="searchPanel" />
				<dx-item location="before" name="addRowButton" css-class="force-primary-color" />
				<dx-item location="after" name="dateLengthButton">
					<dx-button icon="refresh" hint="Refresh data" />
				</dx-item>
				<dx-item name="exportButton" location="after" />
				<dx-item name="columnChooserButton" location="after" />
			</dx-toolbar>
			<dx-scrolling :useNative="true" />
			<dx-export :enabled="true" :formats="['xlsx', 'pdf']" />
			<dx-editing :allow-adding="true" :allow-updating="true" mode="form">
			</dx-editing>

			<dx-column data-field="id" data-type="number" caption="ID" :width="80" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="title" caption="Title" :width="300" :allow-header-filtering="false" />
			<dx-column data-field="slug" caption="Group URL (Slug)" :width="200" :allow-header-filtering="false" />
			<dx-column data-field="members_count" data-type="number" caption="Members" :width="100" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="pages_count" data-type="number" caption="Pages" :width="100" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="created_at" data-type="datetime" caption="Group created" :width="180" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="updated_at" data-type="datetime" caption="Group updated" :width="180" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
		</dx-data-grid>
	</div>
</template>

<style>
.dx-toolbar-item.force-primary-color .dx-button {
	background-color: var(--dx-color-primary);
}
</style>

<script setup>
import CustomStore from 'devextreme/data/custom_store';
import DxDataGrid, { DxColumn, DxFilterRow, DxPager, DxPaging, DxColumnChooser, DxPosition, DxStateStoring, DxToolbar, DxItem, DxSearchPanel, DxScrolling, DxHeaderFilter, DxExport, DxEditing, DxFormItem } from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue/button';
import { inject } from "vue";
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { jsPDF } from 'jspdf';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import GroupCache from '@/groups';

const $http = inject('$http');
// TODO: Optimize this to just return a refresh in our REST response!
const dataSourceConfig = new CustomStore({
	key: 'id',
	loadMode: 'raw',
	remoteOperations: false,
	load: () => {
		return $http.get('/group?stats=1').then(response => response.data);
	},
	insert: (values) => {
		return $http.post('/group', values).then(response => {
			GroupCache.fetchGroupList();	// Queue up a manual refresh of the GroupCache for our menu for right now.
			return response.data;
		});
	},
	update: (key, values) => {
		return $http.patch(`/group/${key}`, values).then(response => {
			GroupCache.fetchGroupList();	// Queue up a manual refresh of the GroupCache for our menu for right now.
			return response.data;
		});
	},
});

const onExporting = (e) => {
	let dateCode = (new Date()).toISOString().substring(0, 10);
	if (e.format == 'xlsx') {
		const workbook = new Workbook();
		const worksheet = workbook.addWorksheet('Main sheet');
		exportDataGrid({
			component: e.component,
			worksheet: worksheet,
			customizeCell: function(options) {
				options.excelCell.font = { name: 'Arial', size: 12 };
				options.excelCell.alignment = { horizontal: 'left' };
			} 
		}).then(function() {
			workbook.xlsx.writeBuffer()
				.then(function(buffer) {
					saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Groups-' + dateCode + '.xlsx');
				});
		});
	} else if (e.format == 'pdf') {
		const doc = new jsPDF();
		exportDataGridToPdf({
			jsPDFDocument: doc,
			component: e.component
		}).then(() => {
			doc.save('Employees-' + dateCode + '.pdf');
		});
	}
};

</script>
